"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import {
  MapPin,
  Clock,
  Banknote,
  Briefcase,
  Search,
} from "lucide-react"
import { Input } from "@/components/ui/input"
import type { JobBoardListing } from "@/types"
import { formatDaysAgo } from "@/lib/format-date"
import { stripHtml } from "@/lib/html-utils"

interface JobListingsWithSearchProps {
  jobs: JobBoardListing[]
  orgSlug: string
  basePath?: string
}

export function JobListingsWithSearch({
  jobs,
  orgSlug,
  basePath = "/jobs",
}: JobListingsWithSearchProps) {
  const [search, setSearch] = useState("")

  const filteredJobs = useMemo(() => {
    if (!search.trim()) return jobs
    const q = search.toLowerCase()
    return jobs.filter(
      (job) =>
        job.role.toLowerCase().includes(q) ||
        (job.department && job.department.toLowerCase().includes(q)) ||
        (job.location && job.location.toLowerCase().includes(q)) ||
        job.employment_type.toLowerCase().includes(q)
    )
  }, [jobs, search])

  if (jobs.length === 0) {
    return (
      <div className="text-center py-16">
        <Briefcase className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h2 className="mt-4 text-lg font-semibold">
          No open positions right now
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Check back later for new opportunities.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {jobs.length > 3 && (
        <div className="relative">
          <label htmlFor="job-search" className="sr-only">
            Search open positions
          </label>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="job-search"
            type="search"
            placeholder="Search by title, department, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {filteredJobs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">
            No positions match your search.
          </p>
        </div>
      ) : (
        filteredJobs.map((job) => (
          <Link
            key={job.link_id}
            href={`${basePath}/${orgSlug}/${job.link_id}`}
            className="group block rounded-xl border bg-card p-6 shadow-sm transition-all hover:border-primary/20 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <h2 className="text-lg font-semibold transition-colors group-hover:text-primary group-focus-visible:text-primary">
              {job.role}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {job.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{job.location}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{job.employment_type}</span>
              </div>
              {job.pay_range && (
                <div className="flex items-center gap-1">
                  <Banknote className="h-3.5 w-3.5" />
                  <span>{job.pay_range}</span>
                </div>
              )}
              {job.department && (
                <div className="flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>{job.department}</span>
                </div>
              )}
            </div>
            {job.description && (
              <p className="mt-3 text-sm text-muted-foreground line-clamp-2">
                {(() => {
                  // Keep paragraph and line breaks apart so "Location: …" and
                  // "Reports to: …" lines don't run together in the preview.
                  const plain = job.description
                    .split(/<\/(?:p|li|h[1-6])>|<br\s*\/?>|\n/i)
                    .map(stripHtml)
                    .filter(Boolean)
                    .join(" · ")
                  return plain.length > 150
                    ? plain.slice(0, 150).trimEnd() + "…"
                    : plain
                })()}
              </p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Posted {formatDaysAgo(job.created_at)}
            </p>
          </Link>
        ))
      )}
    </div>
  )
}
